<script setup lang="ts">
import PdsBadge from '@/components/pds/PdsBadge.vue';
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsIcon from '@/components/pds/PdsIcon.vue';
import PdsSegmented from '@/components/pds/PdsSegmented.vue';
import { computed, onMounted, ref } from 'vue';
import PageBar from '../components/PageBar.vue';
import PageTitle from '../components/PageTitle.vue';
import StockDayStepper from '../components/StockDayStepper.vue';
import StockPrintModal from '../components/StockPrintModal.vue';
import StockRangeModal, {
  type StockRange,
} from '../components/StockRangeModal.vue';
import StockSplitModal from '../components/StockSplitModal.vue';
import { compareLines, type StockLine } from '../stock';
import { formatDate, formatTime } from '../format';
import { usePdsOrdersStore } from '../stores/orders.store';
import { usePdsStockStore, type StockDay } from '../stores/stock.store';

type Tab = 'todo' | 'done';
type Window = 'now' | 'before' | 'all';

const BEFORE_HOUR = '16:00';
const DAY_MS = 86_400_000;
const DAY_NAMES: Partial<Record<StockDay, string>> = {
  today: 'Aujourd’hui',
  yesterday: 'Hier',
  tomorrow: 'Demain',
};
const WINDOW_TITLE: Record<Window, string> = {
  now: 'À préparer maintenant',
  before: 'À préparer avant 16 h',
  all: 'À préparer toute la journée',
};

const store = usePdsOrdersStore();
const stock = usePdsStockStore();
const tab = ref<Tab>('todo');
const window = ref<Window>('now');
const ascending = ref(true);
const printing = ref(false);
const choosingRange = ref(false);
const splitting = ref<{ lineId: string; zone: string } | null>(null);

const isLate = (line: StockLine) => line.lateOrders.length > 0;
const isNow = (line: StockLine) => isLate(line) || line.dueAt < BEFORE_HOUR;
const inWindow = (line: StockLine) =>
  window.value === 'all' ||
  (window.value === 'now' ? isNow(line) : line.dueAt < BEFORE_HOUR);

const all = computed(() => stock.zones.flatMap((zone) => zone.lines));
const todo = computed(() => all.value.filter((line) => !line.done));
const doneCount = computed(() => all.value.length - todo.value.length);

const windows = computed(() => [
  {
    value: 'now' as const,
    label: `Maintenant · ${todo.value.filter(isNow).length}`,
  },
  {
    value: 'before' as const,
    label: `Avant 16 h · ${todo.value.filter((l) => l.dueAt < BEFORE_HOUR).length}`,
  },
  { value: 'all' as const, label: `Toute la journée · ${todo.value.length}` },
]);

const sinceOf = (lines: StockLine[]) => {
  const first = [...lines].sort(compareLines)[0];
  return first?.dayLabel ? `${first.dayLabel} ${first.dueAt}` : first?.dueAt;
};

const visibleZones = computed(() =>
  stock.zones
    .map((zone) => {
      const lines = zone.lines
        .filter((line) =>
          tab.value === 'done' ? line.done : !line.done && inWindow(line),
        )
        .sort((a, b) => compareLines(a, b) * (ascending.value ? 1 : -1));
      return {
        name: zone.name,
        lines,
        pieces: lines.reduce((sum, line) => sum + line.quantity, 0),
        since: lines.some(isLate) ? 'maintenant' : sinceOf(lines),
      };
    })
    .filter((zone) => zone.lines.length),
);

const visibleLines = computed(() =>
  visibleZones.value.flatMap((zone) => zone.lines),
);
const pieces = computed(() =>
  visibleLines.value.reduce((sum, line) => sum + line.quantity, 0),
);
const orderCount = computed(
  () => new Set(visibleLines.value.flatMap((line) => line.orders)).size,
);

const tabs: { value: Tab; label: () => string }[] = [
  { value: 'todo', label: () => `À préparer · ${todo.value.length}` },
  { value: 'done', label: () => `Déjà préparé · ${doneCount.value}` },
];

// Le libellé de la période choisie, dates à l'appui.
const periodLabel = computed(() => {
  const { from, to } = stock.range;
  const start = formatDate(from).toLowerCase();
  if (to - from >= DAY_MS) return `${start} → ${formatDate(to).toLowerCase()}`;
  const name = DAY_NAMES[stock.day];
  return name ? `${name} · ${start}` : formatDate(from);
});

const isoDay = (time: number) => {
  const date = new Date(time);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const stepDay = (delta: number) => {
  const target = new Date(stock.range.from);
  target.setDate(target.getDate() + delta);
  const today = new Date(store.now);
  today.setHours(0, 0, 0, 0);
  const offset = Math.round((target.getTime() - today.getTime()) / DAY_MS);
  if (offset === 0 || offset === -1 || offset === 1) {
    stock.day = offset === 0 ? 'today' : offset < 0 ? 'yesterday' : 'tomorrow';
    return;
  }
  stock.customFrom = stock.customTo = isoDay(target.getTime());
  stock.day = 'custom';
};

const customActive = computed(
  () => !!stock.hourFrom || !!stock.hourTo || stock.day === 'custom',
);
const rangeDraft = computed<StockRange>(() => ({
  from: stock.customFrom || isoDay(stock.range.from),
  to: stock.customTo,
  hourFrom: stock.hourFrom,
  hourTo: stock.hourTo,
}));

const applyRange = (range: StockRange) => {
  stock.hourFrom = range.hourFrom;
  stock.hourTo = range.hourTo;
  stock.customFrom = range.from;
  stock.customTo = range.to;
  stock.day = range.from || range.to ? 'custom' : 'today';
  choosingRange.value = false;
};
const resetRange = () => {
  applyRange({ from: '', to: '', hourFrom: '', hourTo: '' });
};

onMounted(() => stock.load());
</script>

<template>
  <PageBar tall>
    <PageTitle
      :title="tab === 'todo' ? 'Ce qu’il faut préparer' : 'Déjà préparé'"
      :subtitle="
        tab === 'todo'
          ? 'Par zone de rangement, puis par heure de retrait · les zones se déclarent dans Réglages'
          : 'Ne pas reprendre · ce qui est préparé ou remis depuis l’ouverture'
      "
    />
    <template #aside>
      <p class="text-encre-douce hidden text-[12px] leading-[17px] lg:block">
        Mise à jour · {{ formatTime(store.now) }}
      </p>
      <div role="tablist" class="flex gap-5 sm:gap-[30px]">
        <button
          v-for="item in tabs"
          :key="item.value"
          type="button"
          role="tab"
          :aria-selected="tab === item.value"
          class="relative h-12 cursor-pointer text-[14px] leading-[22px] whitespace-nowrap sm:text-[18px]"
          :class="tab === item.value ? 'font-semibold' : 'text-encre-douce'"
          @click="tab = item.value"
        >
          {{ item.label() }}
          <span
            v-if="tab === item.value"
            class="bg-encre absolute inset-x-0 bottom-0 h-[2px]"
          ></span>
        </button>
      </div>
    </template>
  </PageBar>

  <main class="no-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-5 sm:px-5">
    <div class="flex flex-wrap items-center gap-[14px] pt-4">
      <StockDayStepper
        :label="periodLabel"
        @previous="stepDay(-1)"
        @next="stepDay(1)"
      />
      <PdsSegmented
        v-if="tab === 'todo' && stock.day === 'today'"
        v-model="window"
        :options="windows"
      />
      <div class="ml-auto flex items-center gap-4">
        <button
          type="button"
          class="flex h-10 cursor-pointer items-center rounded-full px-4 text-[14px] leading-5 font-semibold"
          :class="customActive ? 'bg-encre text-carte' : 'bg-creux text-encre'"
          @click="choosingRange = true"
        >
          Personnalisé
        </button>
        <button
          type="button"
          class="flex h-12 cursor-pointer items-center gap-2 text-[14px] leading-[22px] font-semibold sm:text-[18px]"
          :aria-label="`Trier par heure, ordre ${ascending ? 'croissant' : 'décroissant'}`"
          @click="ascending = !ascending"
        >
          <span
            class="bg-carte border-filet flex size-12 items-center justify-center rounded-[12px] border"
          >
            <PdsIcon name="sort" :size="22" />
          </span>
          Heure
          <PdsIcon :name="ascending ? 'arrow-up' : 'arrow-down'" :size="20" />
        </button>
      </div>
    </div>

    <p class="flex flex-wrap items-baseline gap-x-3 pt-[14px] pb-4">
      <span
        class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
      >
        {{
          tab === 'todo'
            ? stock.day === 'today'
              ? WINDOW_TITLE[window]
              : `À préparer · ${periodLabel}`
            : `Déjà préparé · ne pas reprendre · ${periodLabel}`
        }}
      </span>
      <span class="text-encre-douce text-[12px] sm:ml-auto">
        {{ visibleLines.length }} références · {{ pieces }} pièces ·
        {{ orderCount }} commandes
      </span>
    </p>

    <p
      v-if="stock.status === 'error'"
      role="alert"
      class="text-alerte-texte pb-3 text-[14px]"
    >
      Les commandes de cette période n’ont pas pu être chargées.
    </p>
    <p
      v-if="!visibleZones.length"
      class="text-encre-douce text-[14px] leading-[1.4]"
    >
      {{
        tab === 'todo'
          ? 'Rien à préparer pour le moment.'
          : 'Rien n’est préparé pour le moment.'
      }}
    </p>
    <div v-else class="flex flex-col gap-6">
      <section
        v-for="zone in visibleZones"
        :key="zone.name"
        class="flex flex-col gap-3"
      >
        <header class="flex flex-wrap items-baseline gap-x-3 px-1">
          <h2
            class="text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
          >
            {{ zone.name }}
          </h2>
          <p class="text-encre-douce text-[12px] leading-[17px]">
            {{ zone.lines.length }} références · {{ zone.pieces }} pièces
            <template v-if="tab === 'todo'"> · dès {{ zone.since }}</template>
          </p>
        </header>
        <div
          class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <article
            v-for="line in zone.lines"
            :key="line.id"
            class="bg-carte flex w-full flex-col gap-3 rounded-[24px] p-5"
            data-testid="stock-card"
          >
            <span class="flex items-start gap-3">
              <span
                class="min-w-[36px] leading-[28px] font-bold"
                :class="
                  line.done ? 'text-encre-douce text-[18px]' : 'text-[24px]'
                "
              >
                {{ line.quantity }}
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block text-[18px] leading-[22px]"
                  :class="line.done && 'text-encre-douce'"
                >
                  {{ line.name }}
                </span>
                <span
                  v-if="line.option"
                  class="text-encre-douce block text-[12px] leading-[17px]"
                >
                  {{ line.option }}
                </span>
              </span>
              <PdsBadge v-if="!line.done && isLate(line)" tone="late">
                {{ line.lateOrders.length }} en retard
              </PdsBadge>
            </span>
            <span
              class="flex flex-wrap items-center gap-x-[10px] gap-y-2 text-[14px] leading-[20px]"
            >
              <span
                class="bg-creux text-encre-douce rounded-[8px] px-[10px] py-[2px] text-[12px] leading-[1.45] font-semibold"
              >
                {{ line.parts.map((part) => `#${part.ref}`).join(' ') }}
              </span>
              <template v-if="!line.done">
                <span class="font-semibold">
                  <template v-if="line.dayLabel"
                    >{{ line.dayLabel }} · </template
                  >{{ line.dueAt }}
                </span>
              </template>
            </span>
            <span
              v-if="line.alreadyPrepared"
              class="text-encre-douce flex flex-wrap items-center gap-x-[10px] gap-y-2 text-[12px] leading-[1.45]"
            >
              <span class="font-semibold">
                Déjà préparé · {{ line.alreadyPrepared.quantity }}
              </span>
              <span
                class="bg-creux rounded-[8px] px-[10px] py-[2px] font-semibold"
              >
                {{
                  line.alreadyPrepared.parts
                    .map((part) => `#${part.ref}`)
                    .join(' ')
                }}
              </span>
            </span>
            <div class="flex">
              <PdsButton
                variant="second"
                @click="splitting = { lineId: line.id, zone: zone.name }"
              >
                Voir plus
              </PdsButton>
            </div>
          </article>
        </div>
      </section>
    </div>
  </main>

  <StockSplitModal
    v-if="splitting"
    :line-id="splitting.lineId"
    :zone="splitting.zone"
    @close="splitting = null"
  />

  <StockRangeModal
    v-if="choosingRange"
    :initial="rangeDraft"
    @close="choosingRange = false"
    @apply="applyRange"
    @reset="resetRange"
  />

  <StockPrintModal
    v-if="printing"
    :zones="visibleZones"
    :done="all.filter((line) => line.done)"
    @close="printing = false"
  />

  <footer
    class="bg-carte border-filet flex min-h-[94px] shrink-0 items-center justify-end border-t px-4 py-4 sm:px-5"
  >
    <PdsButton fit :disabled="!visibleLines.length" @click="printing = true">
      Imprimer la liste
    </PdsButton>
  </footer>
</template>
